import { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { BookOpen, ExternalLink, Copy, Check, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";

const GUIDES = {
  mercado_livre: {
    label: "Mercado Livre",
    color: "#FFE600",
    text: "#2D3277",
    portal: { name: "developers.mercadolibre.com.br", url: "https://developers.mercadolibre.com.br/" },
    auth_kind: "OAuth 2.0",
    fields: [
      ["client_id", "Vai em Platform Settings → mercado_livre → client_id"],
      ["client_secret", "Platform Settings → mercado_livre → client_secret"],
      ["webhook_secret", "(opcional) chave para validar HMAC dos webhooks"],
    ],
    steps: [
      {
        title: "Crie sua conta de desenvolvedor",
        body: [
          "Acesse https://developers.mercadolibre.com.br/ e faça login com a conta Mercado Livre que será a CONTA-MÃE do seu app.",
          "Você precisa ter telefone confirmado e ser maior de idade. Não é a conta do vendedor final — é a conta da sua empresa que vai prover o serviço para os vendedores.",
        ],
      },
      {
        title: "Crie um novo App",
        body: [
          "No painel, vá em \"Meus aplicativos\" → \"Criar novo aplicativo\".",
          "Nome: \"Vyntric\" (ou o nome do seu HUB).",
          "Descrição curta: ex. \"Integração de pedidos e notas fiscais para Mercado Livre\".",
          "URL do site: a URL pública do seu HUB (ex. https://omni-shipping.emergent.host).",
        ],
      },
      {
        title: "Configure os escopos (scopes)",
        body: [
          "Marque os escopos:",
          "• read — para listar pedidos",
          "• write — para atualizar status e enviar NF",
          "• offline_access — necessário para refresh tokens",
        ],
      },
      {
        title: "Configure o Redirect URI",
        body: [
          "No campo \"Redirect URIs\" cole EXATAMENTE a URL de callback do Vyntric (veja no topo deste guia).",
          "É essa URL que o Mercado Livre vai usar para devolver o usuário após o OAuth. Se digitar errado, o login falha.",
        ],
      },
      {
        title: "Configure as notificações (webhooks)",
        body: [
          "Em \"Notificações de tópicos\", marque pelo menos:",
          "• orders_v2 — novos pedidos",
          "• payments — atualizações de pagamento",
          "• shipments — atualizações de envio",
          "URL de notificação: {WEBHOOK_URL}/api/webhooks/mercado_livre",
        ],
      },
      {
        title: "Copie as credenciais",
        body: [
          "Salve o app. Você vai receber:",
          "• App ID = client_id",
          "• Secret Key = client_secret",
          "Cole esses dois em Credenciais Plataforma → Mercado Livre e clique \"Salvar credenciais\".",
        ],
      },
      {
        title: "Conecte a conta do vendedor",
        body: [
          "Pronto, agora qualquer empresa cliente vai em Integrações → Nova integração → Mercado Livre → Conectar via OAuth.",
          "O vendedor aprova uma única vez na tela do ML e os pedidos começam a chegar no Vyntric automaticamente.",
        ],
      },
    ],
  },

  shopee: {
    label: "Shopee",
    color: "#EE4D2D",
    text: "#FFFFFF",
    portal: { name: "open.shopee.com", url: "https://open.shopee.com/" },
    auth_kind: "Partner Sign (HMAC-SHA256)",
    fields: [
      ["partner_id", "Platform Settings → shopee → partner_id (numérico)"],
      ["partner_key", "Platform Settings → shopee → partner_key (string secreta)"],
    ],
    steps: [
      {
        title: "Registre-se como Partner",
        body: [
          "Acesse https://open.shopee.com/ e clique em \"Become a Developer\" → \"Sign up\".",
          "Selecione \"Brazil\" como mercado principal.",
          "A aprovação do cadastro pode levar 1-3 dias úteis (Shopee revisa manualmente).",
        ],
      },
      {
        title: "Crie um App",
        body: [
          "Após aprovação, no console vá em \"App Console\" → \"Create App\".",
          "Tipo: \"Cross-Border\" se você atende vendedores que vendem em vários países, ou \"Local\" se apenas Brasil.",
          "Categorias: marque \"Order Management\", \"Logistics\", \"Product Management\".",
        ],
      },
      {
        title: "Obtenha Partner ID e Partner Key",
        body: [
          "Após criar o app, copie:",
          "• Partner ID — número único do parceiro",
          "• Partner Key — chave secreta (HMAC) usada para assinar todas as requisições",
          "Importante: Shopee NÃO usa OAuth 2.0 padrão — é um sistema de assinatura HMAC-SHA256 onde cada request é assinada com a Partner Key.",
        ],
      },
      {
        title: "Configure o Redirect (Authorization Callback)",
        body: [
          "Em \"App Settings\" → \"Live Configuration\", configure:",
          "• Callback URL: {CALLBACK_URL}",
          "• Push URL (webhook): {WEBHOOK_URL}/api/webhooks/shopee",
        ],
      },
      {
        title: "Habilite os endpoints",
        body: [
          "Na aba \"API List\", habilite no mínimo:",
          "• order.get_order_list, order.get_order_detail",
          "• logistics.get_shipping_parameter, logistics.create_shipping_document",
          "• shop.get_shop_info",
          "Cada endpoint precisa ser ativado individualmente.",
        ],
      },
      {
        title: "Configure credenciais no Vyntric",
        body: [
          "Em Credenciais Plataforma → Shopee, cole:",
          "• partner_id",
          "• partner_key",
          "Salve. As lojas dos vendedores conectam depois via Integrações → Nova → Shopee → Conectar via OAuth.",
        ],
      },
    ],
  },

  tiny_erp: {
    label: "Tiny ERP",
    color: "#15457A",
    text: "#FFFFFF",
    portal: { name: "erp.tiny.com.br", url: "https://erp.tiny.com.br/" },
    auth_kind: "OAuth 2.0 (Keycloak)",
    fields: [
      ["client_id", "Platform Settings → tiny_erp → client_id"],
      ["client_secret", "Platform Settings → tiny_erp → client_secret"],
    ],
    steps: [
      {
        title: "Crie sua conta Tiny (se ainda não tiver)",
        body: [
          "Acesse https://erp.tiny.com.br/ e crie uma conta. O plano grátis funciona para testes; produção exige plano pago.",
          "Tenha o CNPJ da empresa e dados fiscais prontos.",
        ],
      },
      {
        title: "Habilite a API v3",
        body: [
          "Logado, vá em Configurações → API → \"Tiny API v3 (OAuth2)\".",
          "Importante: a API v2 (token estático) está sendo descontinuada. Use sempre a v3.",
          "Clique em \"Criar novo App\" / \"Solicitar acesso à API v3\".",
        ],
      },
      {
        title: "Configure o App OAuth no Tiny",
        body: [
          "Nome do App: \"Vyntric HUB\" (ou seu nome).",
          "Redirect URI: cole EXATAMENTE a URL de callback do Vyntric (veja no topo).",
          "Escopos: marque \"openid\", \"profile\" e todos os escopos de leitura/escrita necessários (notas fiscais, pedidos, produtos, estoque).",
        ],
      },
      {
        title: "Anote Client ID e Secret",
        body: [
          "Após salvar, o Tiny exibe:",
          "• Client ID",
          "• Client Secret (anote AGORA — só aparece uma vez!)",
        ],
      },
      {
        title: "Configure no Vyntric",
        body: [
          "Em Credenciais Plataforma → Tiny ERP, cole client_id e client_secret. Salve.",
          "Cada empresa que usa o Vyntric vai conectar a conta Tiny dela via Integrações → Nova → Tiny ERP → Conectar via OAuth.",
        ],
      },
      {
        title: "Teste com emissão real de NF",
        body: [
          "Recomendado: comece em ambiente de HOMOLOGAÇÃO no Tiny (configure em Configurações → Notas Fiscais → Ambiente).",
          "Emita 2-3 NF de teste do Vyntric e confirme que aparece no Tiny com status \"Autorizada\".",
          "Só depois mude para PRODUÇÃO.",
        ],
      },
    ],
  },

  bling: {
    label: "Bling ERP",
    color: "#003D9C",
    text: "#FFFFFF",
    portal: { name: "developer.bling.com.br", url: "https://developer.bling.com.br/" },
    auth_kind: "OAuth 2.0 (Authorization Code + refresh rotativo)",
    fields: [
      ["client_id", "Platform Settings → bling → client_id (do App criado no portal do Bling)"],
      ["client_secret", "Platform Settings → bling → client_secret"],
    ],
    steps: [
      {
        title: "Crie sua conta de desenvolvedor",
        body: [
          "Acesse https://developer.bling.com.br/ e faça login usando a mesma conta Bling da sua empresa.",
          "Não é preciso conta separada — usa a própria assinatura Bling.",
        ],
      },
      {
        title: "Crie um novo App",
        body: [
          "No painel do developer, vá em \"Meus Aplicativos\" → \"Criar aplicativo\".",
          "Nome: \"Vyntric Multimarketplace\" (ou o nome do seu HUB).",
          "Categoria: qualquer (Marketplace / Logística).",
          "Aceite os termos.",
        ],
      },
      {
        title: "Selecione os escopos (permissões)",
        body: [
          "Marque no mínimo:",
          "• Pedidos - Vendas (leitura e escrita) — necessário para listar pedidos e aplicar desconto",
          "• Situações (leitura) — para saber qual situação corresponde a \"Aprovado\"",
          "Você pode marcar outros escopos se planeja usar produtos, estoque etc futuramente.",
        ],
      },
      {
        title: "Configure o Link de Redirecionamento",
        body: [
          "No campo \"Link de redirecionamento\" cole EXATAMENTE (sem barra no final):",
          "https://SEU-DOMINIO/api/oauth/callback",
          "Ex.: https://vyntric.com.br/api/oauth/callback",
          "O Bling valida essa URL EXATAMENTE. Se não bater, o OAuth falha com \"invalid_redirect_uri\".",
        ],
      },
      {
        title: "Copie Client ID + Client Secret",
        body: [
          "Depois de salvar, o Bling mostra os dois valores. Copie ambos.",
          "O Client Secret só aparece UMA vez — se perder, gere um novo.",
        ],
      },
      {
        title: "Cole no Vyntric",
        body: [
          "No Vyntric: menu → \"Credenciais de Plataforma\" → card Bling ERP.",
          "Cole client_id e client_secret. Salve.",
        ],
      },
      {
        title: "Autorize a conta",
        body: [
          "Menu → \"Integrações\" → \"Nova integração\" → tipo \"Bling ERP\".",
          "Salve. No card gerado clique em \"Conectar via OAuth\".",
          "Você é redirecionado ao Bling → confirma o acesso do App → volta para o Vyntric já conectado.",
        ],
      },
      {
        title: "Aplicando descontos",
        body: [
          "Menu → \"Desconto Bling\" → \"Buscar pedidos\".",
          "Marque os pedidos, informe o percentual e clique em Aplicar.",
          "O Vyntric envia PUT /pedidos/vendas/{id} com desconto = {valor: X, unidade: PERCENTUAL}.",
          "A operação é verificada re-lendo o pedido. Se o Bling não persistir, o pedido é marcado como falha (não gera falso-sucesso).",
        ],
      },
    ],
  },

  amazon: {
    label: "Amazon",
    color: "#232F3E",
    text: "#FF9900",
    portal: { name: "Amazon Developer Console", url: "https://developer-docs.amazon.com/sp-api/" },
    auth_kind: "LWA OAuth + AWS Signature v4",
    sandbox: true,
    steps: [
      {
        title: "Conta de Seller Central",
        body: [
          "Cadastre-se em sellercentral.amazon.com.br como vendedor profissional.",
          "Você precisa do CNPJ válido e conta bancária.",
        ],
      },
      {
        title: "Solicite acesso à SP-API",
        body: [
          "Em Seller Central, vá em \"Apps and Services\" → \"Develop Apps\".",
          "Crie um novo \"App Profile\" como Public ou Private. Selecione \"Self-authorization\" para começar.",
          "Aprovação da Amazon: até 2 semanas.",
        ],
      },
      {
        title: "Configure LWA (Login with Amazon)",
        body: [
          "Em developer.amazon.com → \"Login with Amazon\", crie um Security Profile.",
          "Anote:",
          "• LWA Client ID",
          "• LWA Client Secret",
        ],
      },
      {
        title: "Configure IAM no AWS",
        body: [
          "No console AWS, crie um IAM User com policy \"AmazonSellingPartnerAPI\".",
          "Anote Access Key e Secret Key.",
          "A Amazon assina requests com AWS Signature v4 — não é OAuth puro.",
        ],
      },
      {
        title: "Status no Vyntric",
        body: [
          "Atualmente o conector Amazon está em SANDBOX (mock). Toda a UI / fluxo / criptografia de credencial já está pronta — basta substituir a classe AmazonConnector em /app/backend/connectors.py por uma implementação real usando python-amazon-sp-api ou httpx + LWA + AWS Sig v4.",
          "Quando as credenciais reais chegarem, a promoção do mock → real leva ~1 dia.",
        ],
      },
    ],
  },

  magalu: {
    label: "Magalu",
    color: "#0086FF",
    text: "#FFFFFF",
    portal: { name: "developers.magalu.com", url: "https://developers.magalu.com/" },
    auth_kind: "API Key + OAuth",
    sandbox: true,
    steps: [
      {
        title: "Vire parceiro Magalu Marketplace",
        body: [
          "Acesse marketplace.magalu.com.br e clique em \"Quero vender\". Aprovação em ~5 dias úteis.",
          "É preciso CNPJ, NF de produtos, e mínimo de SKUs ativos.",
        ],
      },
      {
        title: "Acesse o IDMagalu",
        body: [
          "Após aprovado, entre em https://developers.magalu.com e crie um App OAuth.",
          "Anote: Client ID, Secret ID, API Key.",
        ],
      },
      {
        title: "Status no Vyntric",
        body: [
          "Conector Magalu está como SANDBOX (mock) no Vyntric. Implementação real fica em /app/backend/connectors.py:MagaluConnector.",
        ],
      },
    ],
  },

  tiktok_shop: {
    label: "TikTok Shop",
    color: "#000000",
    text: "#FE2C55",
    portal: { name: "partner.tiktokshop.com", url: "https://partner.tiktokshop.com/" },
    auth_kind: "Partner OAuth + Sign",
    sandbox: true,
    steps: [
      {
        title: "Cadastre-se como partner",
        body: [
          "Acesse https://partner.tiktokshop.com/ e crie conta de desenvolvedor.",
          "Aprovação manual pelo time TikTok — varia, mas geralmente 3-10 dias úteis.",
        ],
      },
      {
        title: "Crie um App e obtenha credenciais",
        body: [
          "Em \"App Management\" → \"Create App\".",
          "Anote:",
          "• App Key",
          "• App Secret",
        ],
      },
      {
        title: "Status no Vyntric",
        body: [
          "Conector TikTok Shop está como SANDBOX (mock) no Vyntric. Promoção a real apenas substitui a classe TikTokShopConnector.",
        ],
      },
    ],
  },

  shein: {
    label: "Shein",
    color: "#000000",
    text: "#FFFFFF",
    portal: { name: "open.sheincorp.com", url: "https://open.sheincorp.com/" },
    auth_kind: "B2B Partner OAuth",
    sandbox: true,
    steps: [
      {
        title: "Aprovação B2B manual",
        body: [
          "Shein NÃO tem auto-cadastro. Você precisa entrar em contato comercial via open.sheincorp.com e justificar o caso de uso.",
          "Aprovação típica: 2-4 semanas.",
        ],
      },
      {
        title: "Receba credenciais",
        body: [
          "Após contratação, o time Shein envia client_id e client_secret por canal seguro.",
        ],
      },
      {
        title: "Status no Vyntric",
        body: [
          "Conector Shein está como SANDBOX (mock). Substituir SheinConnector quando obtiver credenciais reais.",
        ],
      },
    ],
  },
};

const ORDER = ["mercado_livre", "shopee", "tiny_erp", "amazon", "magalu", "tiktok_shop", "shein"];

export default function IntegrationGuideDialog({ open, onClose }) {
  const callbackUrl = useMemo(() => `${window.location.origin}/api/oauth/callback`, []);
  const webhookBase = useMemo(() => window.location.origin, []);
  const [copied, setCopied] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      toast.success("Copiado!");
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const renderBody = (line) =>
    line
      .replace(/\{CALLBACK_URL\}/g, callbackUrl)
      .replace(/\{WEBHOOK_URL\}/g, webhookBase);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="integration-guide-dialog"
        className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col gap-0"
      >
        <DialogHeader className="p-6 pb-3 border-b border-slate-200 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-2xl font-black tracking-tighter">
            <BookOpen className="w-6 h-6 text-[#002EA6]" />
            Guia de Integração
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 mt-1">
            Passo a passo de como cadastrar os Apps em cada marketplace e ERP, e plugar no Vyntric.
          </DialogDescription>

          {/* Callback URL banner */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="border border-[#002EA6] bg-[#002EA6]/5 px-3 py-2.5">
              <div className="text-[9px] uppercase tracking-widest font-bold text-[#002EA6] mb-1">
                Callback URL (OAuth)
              </div>
              <div className="flex items-center gap-2">
                <code data-testid="callback-url" className="text-[11px] font-mono text-slate-900 truncate flex-1">
                  {callbackUrl}
                </code>
                <button
                  data-testid="copy-callback"
                  onClick={() => copy(callbackUrl, "cb")}
                  className="p-1 text-slate-600 hover:text-[#002EA6]"
                  title="Copiar"
                >
                  {copied === "cb" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
            <div className="border border-emerald-300 bg-emerald-50 px-3 py-2.5">
              <div className="text-[9px] uppercase tracking-widest font-bold text-emerald-800 mb-1">
                Webhook Base URL
              </div>
              <div className="flex items-center gap-2">
                <code data-testid="webhook-url" className="text-[11px] font-mono text-slate-900 truncate flex-1">
                  {webhookBase}/api/webhooks/&lt;provider&gt;
                </code>
                <button
                  data-testid="copy-webhook"
                  onClick={() => copy(webhookBase, "wh")}
                  className="p-1 text-slate-600 hover:text-emerald-700"
                  title="Copiar"
                >
                  {copied === "wh" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="mercado_livre" className="flex-1 flex flex-col min-h-0">
          <TabsList className="w-full px-6 h-auto p-1.5 bg-slate-100 rounded-none border-b border-slate-200 flex flex-wrap justify-start gap-1 flex-shrink-0">
            {ORDER.map((k) => {
              const g = GUIDES[k];
              return (
                <TabsTrigger
                  key={k}
                  value={k}
                  data-testid={`guide-tab-${k}`}
                  className="text-xs font-bold uppercase tracking-wider data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
                >
                  {g.label}
                  {g.sandbox && (
                    <span className="ml-1.5 text-[8px] px-1 py-0 bg-amber-100 text-amber-800 rounded">
                      sandbox
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {ORDER.map((k) => {
            const g = GUIDES[k];
            return (
              <TabsContent
                key={k}
                value={k}
                className="flex-1 min-h-0 m-0 data-[state=inactive]:hidden"
              >
                <ScrollArea className="h-full">
                  <div className="p-6 space-y-5" data-testid={`guide-content-${k}`}>
                    {/* Banner */}
                    <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-200">
                      <div className="flex items-center gap-3">
                        <div
                          className="px-3 py-1.5 text-sm font-black tracking-tighter"
                          style={{ background: g.color, color: g.text }}
                        >
                          {g.label}
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                            Tipo de autenticação
                          </div>
                          <div className="text-sm font-bold text-slate-900">{g.auth_kind}</div>
                        </div>
                      </div>
                      <a
                        href={g.portal.url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`guide-portal-${k}`}
                        className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white flex items-center gap-1.5 self-start"
                      >
                        {g.portal.name} <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>

                    {g.sandbox && (
                      <div className="border border-amber-300 bg-amber-50 px-3 py-2.5 flex gap-2 items-start">
                        <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-900">
                          Conector em modo <span className="font-bold">SANDBOX</span> (mock determinístico). A estrutura
                          está pronta — basta substituir a classe correspondente em{" "}
                          <span className="font-mono">/app/backend/connectors.py</span> pelo cliente real quando as
                          credenciais chegarem.
                        </div>
                      </div>
                    )}

                    {g.fields && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
                          Campos que você vai precisar coletar
                        </div>
                        <div className="space-y-1">
                          {g.fields.map(([name, where]) => (
                            <div key={name} className="flex items-baseline gap-2 text-sm">
                              <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 font-bold">{name}</code>
                              <span className="text-slate-600 text-xs">→ {where}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Steps */}
                    <ol className="space-y-4 mt-4">
                      {g.steps.map((step, idx) => (
                        <li
                          key={idx}
                          className="border border-slate-200 bg-white p-4 flex gap-4"
                          data-testid={`guide-step-${k}-${idx}`}
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-[#002EA6] text-white flex items-center justify-center text-sm font-black">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-slate-900 mb-2">{step.title}</div>
                            <ul className="space-y-1.5">
                              {step.body.map((line, j) => (
                                <li key={j} className="text-xs text-slate-700 leading-relaxed">
                                  {renderBody(line).split(/(https?:\/\/\S+)/).map((part, idx2) =>
                                    part.match(/^https?:\/\//) ? (
                                      <a
                                        key={idx2}
                                        href={part}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[#002EA6] underline font-mono"
                                      >
                                        {part}
                                      </a>
                                    ) : (
                                      <span key={idx2}>{part}</span>
                                    )
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </li>
                      ))}
                    </ol>

                    <div className="border-t border-slate-200 pt-4 flex items-start gap-2 text-xs text-slate-600">
                      <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        Após configurar as credenciais aqui, vá em{" "}
                        <a href="/integrations" className="font-bold text-[#002EA6] underline">
                          Integrações
                        </a>{" "}
                        para conectar a primeira conta de vendedor via OAuth.
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>
            );
          })}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
